import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AuditEvent, type InboxItem, type RiskFinding } from '../shared/api';
import {
  Empty, ErrorNote, RiskPill, Skeleton, StatePill,
  formatDateTime, relativeTime, titleise, useToast,
} from '../shared/ui';

/* ── Approvals inbox ───────────────────────────────────────────────────── */

export function ApprovalsPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['inbox'],
    queryFn: () => api.get<{ items: InboxItem[]; total: number }>('/approvals/inbox'),
    refetchInterval: 30_000,
  });
  const navigate = useNavigate();

  if (isLoading) return <Skeleton rows={5} />;
  if (error) return <ErrorNote error={error} />;

  const items = data?.items ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Waiting on you</div>
          <h1 className="page-title">Approvals</h1>
          <p className="page-sub">
            Only orders that have reached your level appear here. Levels are sequential.
          </p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="card">
          <Empty
            title="Your queue is clear"
            hint="Nothing is waiting for your decision. Orders appear here once earlier levels have approved."
            action={<Link className="btn btn-ghost" to="/orders">Browse all orders</Link>}
          />
        </div>
      ) : (
        <div className="col gap-10">
          {items.map((o) => (
            <div
              key={o.id}
              className="card row between row-wrap gap-14"
              style={{
                cursor: 'pointer',
                borderColor: o.requires_reapproval ? 'var(--breach)' : undefined,
              }}
              onClick={() => navigate(`/orders/${o.id}`)}
              tabIndex={0}
              role="button"
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/orders/${o.id}`); }}
            >
              <div className="grow">
                <div className="row gap-10 row-wrap">
                  <span className="vno" style={{ fontSize: 14 }}>{o.voucher_no}</span>
                  <StatePill state={o.state} />
                  {o.risk && o.risk.count > 0 && (
                    <RiskPill level={o.risk.level} count={o.risk.count} />
                  )}
                  {o.requires_reapproval && (
                    <span className="pill pill-bad">Changed since you approved</span>
                  )}
                </div>
                <div className="faint mt-6" style={{ fontSize: 13 }}>
                  {titleise(o.purpose)} · {o.line_count} line{o.line_count === 1 ? '' : 's'} ·{' '}
                  {o.bank_account_label} · raised by {o.created_by_name}
                </div>
                {o.risk?.top && (
                  <div style={{ fontSize: 12.5, marginTop: 6, color: 'var(--watch)' }}>
                    {o.risk.top}
                  </div>
                )}
              </div>

              <div className="right">
                <div
                  className="mono"
                  style={{
                    fontSize: 20, fontWeight: 700,
                    color: o.txn_type === 'DEBIT' ? 'var(--breach)' : 'var(--verify)',
                  }}
                >
                  {o.total.formatted}
                </div>
                <div className="ghost" style={{ fontSize: 11.5, marginTop: 3 }}>
                  Level {o.step.level} · waiting {relativeTime(o.waiting_since)}
                </div>
                <button className="btn btn-primary btn-sm mt-10">Review and decide</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* ── Findings ──────────────────────────────────────────────────────────── */

export function FindingsPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const [state, setState] = useState('OPEN');

  const { data, isLoading, error } = useQuery({
    queryKey: ['findings', state],
    queryFn: () => api.get<RiskFinding[]>(`/agents/suggestions?state=${state}`),
  });

  const decide = useMutation({
    mutationFn: ({ id, accepted }: { id: string; accepted: boolean }) =>
      api.post(`/agents/suggestions/${id}/decide`, { accepted }),
    onSuccess: (_, { accepted }) => {
      qc.invalidateQueries({ queryKey: ['findings'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast(accepted ? 'Marked as a real issue' : 'Dismissed', 'ok');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Could not record that', 'bad'),
  });

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Risk agent</div>
          <h1 className="page-title">Findings</h1>
          <p className="page-sub">
            The agent proposes; you decide. Nothing here blocks a payment — your decisions
            train what it flags next.
          </p>
        </div>
        <select
          className="select"
          style={{ maxWidth: 180 }}
          value={state}
          onChange={(e) => setState(e.target.value)}
          aria-label="Filter findings"
        >
          <option value="OPEN">Open</option>
          <option value="ACCEPTED">Confirmed real</option>
          <option value="DISMISSED">Dismissed</option>
        </select>
      </div>

      {error && <ErrorNote error={error} />}
      {isLoading ? (
        <Skeleton rows={5} />
      ) : !data || data.length === 0 ? (
        <div className="card">
          <Empty
            title={state === 'OPEN' ? 'Nothing flagged' : 'Nothing here'}
            hint="Every order is checked on submission for duplicate invoices, threshold avoidance, recently changed bank details, first-time beneficiaries and unusual amounts."
          />
        </div>
      ) : (
        <div className="col gap-10">
          {data.map((f) => (
            <div key={f.id} className={`card finding ${f.risk}`} style={{ borderRadius: 12 }}>
              <div className="row between row-wrap gap-10">
                <div className="grow">
                  <div className="finding-title">
                    <RiskPill level={f.risk} /> {f.title}
                  </div>
                  <div className="finding-detail">{f.detail}</div>
                  <div className="row gap-10 row-wrap mt-10">
                    <span className="ghost mono" style={{ fontSize: 10.5 }}>
                      {f.code} · {f.confidence}% confidence · {f.agent}
                    </span>
                    {typeof f.evidence?.matched_voucher_no === 'string' && (
                      <span className="ghost" style={{ fontSize: 11 }}>
                        matches {String(f.evidence.matched_voucher_no)}
                      </span>
                    )}
                    <Link
                      className="vno"
                      to={`/orders/${(f as unknown as { entity_id?: string }).entity_id ?? ''}`}
                      style={{ fontSize: 11 }}
                    >
                      Open order
                    </Link>
                  </div>
                </div>

                {state === 'OPEN' && (
                  <div className="row gap-6">
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => decide.mutate({ id: f.id, accepted: false })}
                      disabled={decide.isPending}
                    >
                      Not an issue
                    </button>
                    <button
                      className="btn btn-bad btn-sm"
                      onClick={() => decide.mutate({ id: f.id, accepted: true })}
                      disabled={decide.isPending}
                    >
                      Confirm
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* ── Audit ─────────────────────────────────────────────────────────────── */

export function AuditPage() {
  const toast = useToast();
  const { data, isLoading, error } = useQuery({
    queryKey: ['audit-all'],
    queryFn: () => api.get<AuditEvent[]>('/audit/events?limit=200'),
  });

  const verify = useMutation({
    mutationFn: () => api.get<{ valid: boolean; checked: number; head_hash?: string; broken_at_id?: number }>('/audit/verify'),
    onSuccess: (r) =>
      toast(
        r.valid
          ? `Chain intact across ${r.checked} events`
          : `Chain broken at event ${r.broken_at_id}`,
        r.valid ? 'ok' : 'bad',
      ),
    onError: (e) => toast(e instanceof Error ? e.message : 'Could not verify', 'bad'),
  });

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Assurance</div>
          <h1 className="page-title">Audit trail</h1>
          <p className="page-sub">
            Append-only and hash-chained. Each entry commits to its predecessor, so any
            alteration breaks the chain and can be detected.
          </p>
        </div>
        <button
          className="btn btn-ghost"
          onClick={() => verify.mutate()}
          disabled={verify.isPending}
        >
          {verify.isPending ? 'Verifying…' : 'Verify chain integrity'}
        </button>
      </div>

      {error && <ErrorNote error={error} />}
      {isLoading ? (
        <Skeleton rows={9} />
      ) : !data || data.length === 0 ? (
        <div className="card">
          <Empty title="No activity yet" hint="Every action taken in the system is recorded here." />
        </div>
      ) : (
        <div className="card card-flush">
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th>When</th>
                  <th>Who</th>
                  <th>What happened</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.map((e) => (
                  <tr key={e.id}>
                    <td className="mono ghost">{e.id}</td>
                    <td className="mono faint" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                      {formatDateTime(e.occurred_at)}
                    </td>
                    <td style={{ fontSize: 12.5 }}>{e.actor_name ?? 'system'}</td>
                    <td style={{ fontSize: 13 }}>{e.summary ?? '—'}</td>
                    <td>
                      <span className="mono ghost" style={{ fontSize: 11 }}>{e.action}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
